import { Seo } from "../components/Seo";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { GoogleButton } from "../components/GoogleButton";
import { OtpVerify } from "../components/OtpVerify";

export default function Login() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needTotp, setNeedTotp] = useState(false);
  const [needVerify, setNeedVerify] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const body = { email, password };
      if (needTotp) body.totp_code = totp;
      const { data } = await api.post("/auth/login", body);
      const user = setSession(data.access_token, data.user);
      toast.success("Welcome back!");
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      if (status === 403 && detail === "EMAIL_NOT_VERIFIED") {
        // send a fresh code and switch to verification step
        try { const { data } = await api.post("/auth/resend-otp", { email }); setDevMode(!!data.dev_mode); } catch (e2) { console.debug("resend-otp failed", e2); }
        setNeedVerify(true);
        toast.message("Please verify your email to continue. We sent you a code.");
      } else if (status === 401 && detail === "TOTP_REQUIRED") {
        setNeedTotp(true);
        toast.message("Enter the 6-digit code from your authenticator app.");
      } else {
        setError(formatApiErrorDetail(detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const onVerified = (data) => {
    const user = setSession(data.access_token, data.user);
    navigate(user.role === "admin" ? "/admin" : "/dashboard");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <Seo path="/login" title="Sign in" description="Sign in to PNR Decode to convert GDS itineraries, brand them, and share client-ready flight documents." />
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <img src="/pnr-decode-wordmark.png" alt="PNR Decode" className="h-14 w-auto object-contain" />
        </div>

        {needVerify ? (
          <>
            <OtpVerify email={email.toLowerCase()} devMode={devMode} onVerified={onVerified} />
            <button data-testid="verify-back" onClick={() => setNeedVerify(false)} className="mt-6 text-sm text-slate-500 hover:text-slate-800">← Back to sign in</button>
          </>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-1">Sign in</h1>
            <p className="text-sm text-slate-500 mb-6">Access your dashboard and tools.</p>

            <GoogleButton />
            <div className="my-4 flex items-center gap-3 text-xs text-slate-400">
              <div className="h-px flex-1 bg-slate-200" /> or <div className="h-px flex-1 bg-slate-200" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input data-testid="login-email" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" placeholder="you@example.com" disabled={needTotp} />
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Input data-testid="login-password" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" placeholder="••••••••" disabled={needTotp} />
              </div>
              {needTotp && (
                <div data-testid="login-totp-field">
                  <Label htmlFor="totp" className="text-slate-700 inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-blue-600" /> Authenticator code</Label>
                  <Input data-testid="login-totp" id="totp" inputMode="numeric" maxLength={6} value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))} required className="mt-1 text-center tracking-[0.4em] font-pnr" placeholder="123456" autoFocus />
                </div>
              )}
              {error && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
              <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : needTotp ? "Verify code" : "Sign in"}
              </Button>
            </form>

            <div className="flex items-center justify-between mt-4 text-sm">
              <Link to="/forgot-password" className="text-blue-600 hover:underline" data-testid="forgot-link">Forgot password?</Link>
              <Link to="/register" className="text-slate-600 hover:underline" data-testid="register-link">Create account</Link>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-200 text-center">
              <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors" data-testid="login-home-link">
                <ArrowLeft className="h-4 w-4" /> Go to homepage
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
